import React from 'react';

import styled from 'styled-components';

import { ChartType } from '@/components/charting/types';
import {
  BAR,
  BARLINE,
  LINE,
  PIE,
  PIVOT,
  STACKED_CHART,
  TABLE,
} from '@/components/charting/chartType.constants';
import ModalContent from './ModalContent';

import css from '../css/module/chart-hints.css';

const options: Record<ChartType, [string, JSX.Element, string]> = {
  [TABLE]: [
    'Create a single value chart',
    <span key={TABLE}>
      In the query builder, use the <b>summarize</b> step with <b>1 metric</b>.
    </span>,
    '/video/single-value-min.mp4',
  ],
  [BAR]: [
    'Create a bar chart',
    <span key={BAR}>
      In the query builder, use the <b>summarize</b> step with <b>1 metric</b>{' '}
      and <b>1 or 2 dimensions</b>.
    </span>,
    '/video/barchart-min.mp4',
  ],
  [LINE]: [
    'Create a line chart',
    <span key={LINE}>
      In the query builder, use the <b>summarize</b> step with{' '}
      <b>1 dimension</b> and <b>1 or more metrics</b>.
    </span>,
    '/video/line-min.mp4',
  ],
  [BARLINE]: [
    'Create a mixed bar line chart',
    <span key={BARLINE}>
      In the query builder, use the <b>summarize</b> step with{' '}
      <b>1 dimension</b> and <b>2 metrics</b>.
    </span>,
    '/video/bar-line-min.mp4',
  ],
  [PIE]: [
    'Create a pie chart',
    <span key={PIE}>
      In the query builder, use the <b>summarize</b> step with <b>1 metric</b>{' '}
      and <b>1 dimension</b>.
    </span>,
    '/video/pie-chart-min.mp4',
  ],
  [STACKED_CHART]: [
    'Create a stacked bar chart',
    <span key={STACKED_CHART}>
      In the query builder, use the <b>summarize</b> step with <b>1 metric</b>{' '}
      and <b>2 dimensions</b>.
    </span>,
    '/video/stacked-min.mp4',
  ],
  [PIVOT]: [
    'Create a pivot table',
    <span key={PIVOT}>
      In the query builder, use the <b>summarize</b> step with <b>1 metric</b>{' '}
      and <b>2, 3 or 4 dimensions</b>.
    </span>,
    '/video/pivot-min.mp4',
  ],
};

const VideoHint = styled.video.attrs({
  width: '618',
  height: '400',
  autoPlay: true,
  loop: true,
  muted: true,
  playsInline: true,
  type: 'video/mp4',
})`
  width: 100%;
  borderradius: 4px;
`;

export type ChartHintProps = {
  presentation: ChartType;
};

export const ChartHint: React.FC<ChartHintProps> = ({ presentation }) => {
  const [title, description, videoLink] = options[presentation];

  return (
    <ModalContent small header={title}>
      <div className={css.container}>
        {description}
        <div className={css.videoContainer}>
          <VideoHint src={videoLink} />
        </div>
      </div>
    </ModalContent>
  );
};

export default ChartHint;
